import { ReactNode, useState } from "react";
import { Box, Typography, Stack, Link, IconButton, Drawer, Divider, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import Layout from "../../Layout";
import { useAuth } from "../../AuthProvider";
import { useCustomColors } from "../../hooks/useCustomColors";
import MobileBottomNav, { MOBILE_NAV_HEIGHT } from "./MobileBottomNav";
import { useTranslation } from "react-i18next";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import WalletBalanceCard from "../WalletBalanceCard";
import AppHeader from "../AppHeader";
import BrandLogo from "../BrandLogo";
import { ME_QUERY_KEY, useProfileQuery } from "../../hooks/useProfile";
import { usePlayerLive } from "../../hooks/usePlayerRealtime";
import { ApiProfile, GetProfileResponse, toProfileResponse } from "../../api/getProfile";
import type { Contact } from "../../api/general";
import CTADialog from "./CTADialog";
import ChangePasswordDialog from "./ChangePasswordDialog";
import SupportContact from "../SupportContact";
import LanguageSwitcher from "../language";
import LiveStats from "./LiveStats";
import { PAGE_MAX_WIDTH, PAGE_PX } from "../../utils/layout";
import { brand } from "../../theme";

const LIVE_STAT_RANGES = [
  { dot: brand.info, labelKey: "shell_online", min: 8000, max: 12000, refreshSeconds: 5 },
  { dot: brand.gold, labelKey: "shell_prize_pool", min: 47000, max: 50000, prefix: "RM ", refreshSeconds: 8 },
  { dot: brand.primaryBright, labelKey: "shell_bets_today", min: 250000, max: 600000, refreshSeconds: 3,prefix: "RM " },
  { dot: brand.success, labelKey: "shell_24h_payout", min: 85000, max: 95000, prefix: "RM ", refreshSeconds: 5 },
];

const LEGAL_LINKS = [
  { labelKey: "legal_terms_tab", href: "/terms" },
  { labelKey: "legal_privacy_tab", href: "/privacy" },
];

type DrawerItem = { labelKey: string; href?: string; onOpen?: () => void };

const TABS = [
  { labelKey: "nav_home", href: "/" },
  { labelKey: "nav_play", href: "/game/bet" },
  { labelKey: "nav_history", href: "/history" },
];

interface LottoShellProps {
  children: ReactNode;
}

const LottoShell = ({ children }: LottoShellProps) => {
  const colors = useCustomColors();
  const { palette } = useTheme();
  const isDark = palette.mode === "dark";
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  /* The same `/me` the auth provider polls — read from the shared query rather
     than fetched again, which is what made the shell's balance a second copy of
     a request already in flight. */
  const { data: profile, isError: profileFailed } = useProfileQuery(Boolean(user));
  usePlayerLive<ApiProfile>(
    "me",
    {},
    (nextProfile) =>
      queryClient.setQueryData<GetProfileResponse>(ME_QUERY_KEY, toProfileResponse(nextProfile)),
    { enabled: Boolean(user) }
  );
  const locale = i18n.language === "zh" ? "zh" : "en";
  usePlayerLive<Contact>("contact", { locale }, (contact) => {
    queryClient.setQueryData(["contact", locale], contact);
  });
  const balance = user
    ? profile?.data.wallet?.balance ?? (profileFailed ? 0 : null)
    : null;

  const tabs = user ? TABS : [...TABS, { labelKey: "login", href: "/login" }];

  // Withdraw and Change password are dialogs, not routes, so they live in the side nav only.
  const drawerItems: DrawerItem[] = user
    ? [
        ...TABS.flatMap((tab) =>
          tab.href === "/game/bet"
            ? [tab, { labelKey: "nav_withdraw", onOpen: () => setWithdrawOpen(true) }]
            : [tab]
        ),
        { labelKey: "change_password", onOpen: () => setPasswordOpen(true) },
      ]
    : tabs;

  // One divider style for the whole drawer, so every line matches.
  const dividerSx = { border: 0, height: "1px", background: colors.gradient.divider };

  const drawerItemSx = {
    px: "14px",
    py: "12px",
    borderRadius: "12px",
    color: colors.text.secondary,
    fontSize: "13px",
    fontWeight: 700,
    "&:hover": { color: colors.text.primary, backgroundColor: colors.tint.primary },
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: "100vh",
          background: colors.background.gradient,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          // Inner scrollers own their own scrollbars; the page never scrolls sideways.
          overflowX: "hidden",
          p: 0,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: PAGE_MAX_WIDTH,
          }}
        >
          {/* Brand bar */}
          <AppHeader>
            {user && (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    display: { xs: "none", md: "flex" },
                    height: "38px",
                    px: "14px",
                    gap: "8px",
                    borderRadius: "19px",
                    backgroundColor: isDark ? "rgba(10, 8, 8, 0.75)" : colors.background.bar,
                    border: isDark ? "1px solid rgba(212, 175, 55, 0.26)" : `1px solid ${colors.border.accent}`,
                    boxShadow: isDark ? "inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 4px 12px rgba(0, 0, 0, 0.18)" : "0 2px 8px rgba(45, 33, 26, 0.08)",
                    transition: "background-color 0.2s ease, border-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: isDark ? "rgba(99, 25, 26, 0.20)" : colors.background.panelSecondary,
                      borderColor: isDark ? "rgba(212, 175, 55, 0.45)" : colors.border.accent,
                    },
                  }}
                >
                  <PersonRoundedIcon
                    sx={{ fontSize: "16px", color: colors.accent.gold }}
                  />
                  <Typography
                    sx={{
                      display: { xs: "none", sm: "block" },
                      fontSize: "12.5px",
                      fontWeight: 700,
                      color: isDark ? "#F8F3EE" : colors.text.primary,
                    }}
                  >
                    {user.name}
                  </Typography>
                </Stack>
              </>
            )}
            <IconButton
              onClick={() => setDrawerOpen(true)}
              aria-label="menu"
              sx={{
                width: 38,
                height: 38,
                borderRadius: "19px",
                color: isDark ? "#F8F3EE" : colors.text.primary,
                border: isDark ? "1px solid rgba(212, 175, 55, 0.18)" : `1px solid ${colors.border.accent}`,
                backgroundColor: isDark ? "rgba(10, 8, 8, 0.75)" : colors.background.bar,
                boxShadow: isDark ? "inset 0 1px 0 rgba(255, 255, 255, 0.04), 0 4px 12px rgba(0, 0, 0, 0.18)" : "0 2px 8px rgba(45, 33, 26, 0.08)",
                transition: "background-color 0.2s ease, border-color 0.2s ease",
                "&:hover": {
                  backgroundColor: isDark ? "rgba(77, 17, 19, 0.72)" : colors.background.panelSecondary,
                  borderColor: isDark ? "rgba(212, 175, 55, 0.45)" : colors.border.accent,
                },
              }}
            >
              <MenuRoundedIcon sx={{ fontSize: "18px" }} />
            </IconButton>
          </AppHeader>

          <Box
            sx={{
              px: PAGE_PX,
              pt: { xs: "24px", md: "32px" },
              pb: {
                xs: user ? `calc(${MOBILE_NAV_HEIGHT}px + 24px)` : "24px",
                md: "36px",
              },
              ...(pathname === "/" && {
                "--home-section-gap": { xs: "16px", md: "24px" },
                pt: "var(--home-section-gap)",
                "@media (min-width: 768px) and (max-width: 1024px)": {
                  "--home-section-gap": "12px",
                },
              }),
            }}
          >
            {/* Section tabs */}
            {user && (
              <Box
                sx={{
                  p: "4px",
                  display: { xs: "none", sm: "flex" },
                  width: "fit-content",
                  maxWidth: "100%",
                  overflowX: "auto",
                  gap: "4px",
                  borderRadius: "22px",
                  backgroundColor: colors.background.rail,
                  border: `1px solid ${colors.card.border}`,
                }}
              >
                {tabs.map((tab) => {
                  const isActive =
                    tab.href === "/"
                      ? pathname === "/"
                      : pathname.startsWith(tab.href);
                  return (
                    <Link
                      key={tab.labelKey}
                      href={tab.href}
                      underline="none"
                      sx={{
                        height: "35px",
                        px: "22px",
                        display: "flex",
                        alignItems: "center",
                        borderRadius: "18px",
                        fontSize: "13px",
                        fontWeight: 700,
                        letterSpacing: "0.5px",
                        color: isActive ? "#FFFFFF" : colors.text.secondary,
                        background: isActive ? colors.gradient.brand : "none",
                        boxShadow: isActive
                          ? colors.gradient.brandGlow
                          : "none",
                        "&:hover": { color: isDark ? "#FFFFFF" : colors.text.primary },
                      }}
                    >
                      {t(tab.labelKey)}
                    </Link>
                  );
                })}
              </Box>
            )}

            {pathname === "/" && (
              <Box
                sx={{
                  mt: { xs: 0, sm: "var(--home-section-gap)" },
                }}
              >
                <LiveStats ranges={LIVE_STAT_RANGES} />
              </Box>
            )}

            {children}

            <Box
              sx={{
                mt: { xs: "28px", md: "36px" },
                pt: { xs: "20px", md: "28px" },
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "minmax(0, 1fr) minmax(0, 1fr)",
                  md: "minmax(220px, 1fr) auto minmax(300px, 1fr)",
                },
                alignItems: "center",
                gap: { xs: "18px", sm: "20px 24px", md: "0 32px" },
                position: "relative",
                borderTop: 0,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "2px",
                  background: colors.gradient.divider,
                },
              }}
            >
              <Typography sx={{ fontSize: "12px", color: colors.footer.note }}>
                {t("legal_age_notice")}
              </Typography>

              <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                sx={{
                  gridColumn: { sm: "1 / -1", md: "auto" },
                  gridRow: { sm: "2", md: "auto" },
                  gap: "8px 24px",
                  justifyContent: { xs: "flex-start", md: "center" },
                }}
              >
                {LEGAL_LINKS.map((legal) => (
                  <Link
                    key={legal.href}
                    href={legal.href}
                    underline="hover"
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: colors.footer.link,
                      "&:hover": { color: colors.footer.linkHover },
                      "&:focus-visible": { color: colors.footer.linkHover },
                    }}
                  >
                    {t(legal.labelKey)}
                  </Link>
                ))}
                <Typography
                  onClick={() => setSupportOpen(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") setSupportOpen(true);
                  }}
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: colors.footer.link,
                    cursor: "pointer",
                    "&:hover": { color: colors.footer.supportHover },
                    "&:focus-visible": { color: colors.footer.supportHover },
                  }}
                >
                  {t("dashboard_support")}
                </Typography>
              </Stack>

              <Typography
                sx={{
                  gridColumn: { sm: "2", md: "auto" },
                  gridRow: { sm: "1", md: "auto" },
                  fontSize: "12px",
                  color: colors.footer.note,
                  textAlign: { xs: "left", sm: "right" },
                }}
              >
                {t("footer_copyright", { year: 2026 })}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "min(86vw, 340px)", sm: "340px" },
            p: "20px",
            display: "flex",
            flexDirection: "column",
            minHeight: "100%",
            color: colors.text.primary,
            background: colors.gradient.drawer,
            backgroundImage: colors.gradient.drawer,
            borderRight: `1px solid ${colors.border.accent}`,
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: "18px", gap: "12px" }}>
          <BrandLogo height={32} onClick={() => navigate("/")} />
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: colors.text.secondary }}>
            <Typography sx={{ fontSize: "22px", lineHeight: 1 }}>×</Typography>
          </IconButton>
        </Stack>
        {user && (
          <>
            <WalletBalanceCard balance={balance} name={user.name} onClick={() => setTopUpOpen(true)} />
            <Divider sx={{ ...dividerSx, mt: "32px", mb: "20px" }} />
          </>
        )}
        <Stack sx={{ gap: "6px" }}>
          {drawerItems.map((item) =>
            item.href ? (
              <Link
                key={item.labelKey}
                href={item.href}
                underline="none"
                onClick={() => setDrawerOpen(false)}
                sx={drawerItemSx}
              >
                {t(item.labelKey)}
              </Link>
            ) : (
              <Link
                key={item.labelKey}
                component="button"
                type="button"
                underline="none"
                onClick={() => {
                  setDrawerOpen(false);
                  item.onOpen?.();
                }}
                sx={{ ...drawerItemSx, width: "100%", textAlign: "left" }}
              >
                {t(item.labelKey)}
              </Link>
            )
          )}
        </Stack>

        <Divider sx={{ ...dividerSx, mt: "20px", mb: "18px" }} />
        <Box sx={{ pb: "env(safe-area-inset-bottom)" }}>
        <Box sx={{ mt: "auto", mb: "18px" }}>
          <SupportContact onClick={() => setDrawerOpen(false)} />
        </Box>
          <LanguageSwitcher />
          {user && (
            <Button
              fullWidth
              variant="outlinedDark"
              startIcon={<LogoutRoundedIcon sx={{ fontSize: "16px" }} />}
              onClick={handleLogout}
              sx={{ mt: "14px", height: "42px", borderRadius: "12px" }}
            >
              {t("logout")}
            </Button>
          )}
        </Box>
      </Drawer>

      <CTADialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        link="review"
        title={t("dashboard_support_title")}
        subtitle={t("dashboard_support_subtitle")}
        description={t("dashboard_support_description")}
        note={t("dashboard_support_note")}
        actionLabel={t("top_up_contact_support")}
      />

      <CTADialog
        open={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        mascot
        title={t("withdraw_title")}
        subtitle={t("withdraw_subtitle")}
        description={t("withdraw_description")}
        note={t("withdraw_note")}
        actionLabel={t("top_up_contact_support")}
      />

      <CTADialog
        open={topUpOpen}
        onClose={() => setTopUpOpen(false)}
        mascot
        title={t("top_up_title")}
        subtitle={t("top_up_subtitle")}
        description={t("top_up_description")}
        note={t("top_up_note")}
        actionLabel={t("top_up_contact_support")}
      />

      {user && <ChangePasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />}

      {user && <MobileBottomNav />}
    </Layout>
  );
};

export default LottoShell;
